import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

const DATA = '/blog/comma-compression/data/weight-histograms.json';

interface HistData {
  n_weights: number;
  stddev: number;
  bins: number[];
  actual: number[];
  gauss: number[];
  entropy_bits: { actual: number; gauss: number };
  top_integers: { value: number; count: number; fraction: number }[];
}

const Wrapper = styled.div`
  margin: 2.5rem 0;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-alt);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PanelHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.4rem;
`;

const PanelTitle = styled.div`
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 500;
  color: var(--text);
`;

const Entropy = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
`;

const ChartFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 7;
  background: var(--bg);
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid var(--border);
`;

const Caption = styled.div`
  font-family: var(--font-body);
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin-top: 1.25rem;
`;

const Loading = styled.div`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-muted);
  text-align: center;
  padding: 2.5rem;
`;

const Histogram: React.FC<{
  bins: number[];
  counts: number[];
  maxCount: number;
  color: string;
}> = ({ bins, counts, maxCount, color }) => {
  const w = 800;
  const h = 350;
  const padX = 30;
  const padY = 20;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const barW = innerW / counts.length;

  // x-axis tick labels
  const ticks = [-127, -64, 0, 64, 127];

  return (
    <ChartFrame>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" preserveAspectRatio="none">
        {/* zero line */}
        <line
          x1={padX + (innerW * (0 - bins[0])) / (bins[bins.length - 1] - bins[0])}
          x2={padX + (innerW * (0 - bins[0])) / (bins[bins.length - 1] - bins[0])}
          y1={padY}
          y2={padY + innerH}
          stroke="var(--border)"
          strokeDasharray="3,3"
          strokeWidth={1}
        />

        {/* bars */}
        {counts.map((c, i) => {
          const barH = (c / maxCount) * innerH;
          return (
            <rect
              key={i}
              x={padX + i * barW}
              y={padY + (innerH - barH)}
              width={Math.max(barW * 0.95, 0.8)}
              height={barH}
              fill={color}
            />
          );
        })}

        {/* x-axis ticks */}
        {ticks.map((t) => {
          const x = padX + (innerW * (t - bins[0])) / (bins[bins.length - 1] - bins[0]);
          return (
            <g key={t}>
              <line x1={x} x2={x} y1={padY + innerH} y2={padY + innerH + 4} stroke="var(--text-muted)" strokeWidth={1} />
              <text
                x={x}
                y={padY + innerH + 14}
                textAnchor="middle"
                fontFamily="var(--font-mono)"
                fontSize={10}
                fill="var(--text-muted)"
              >
                {t}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
};

const WeightDistribution: React.FC = () => {
  const [data, setData] = useState<HistData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(DATA)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(String(e)));
  }, []);

  if (error) {
    return (
      <Wrapper>
        <Loading>Failed to load weight-histograms.json: {error}</Loading>
      </Wrapper>
    );
  }
  if (!data) {
    return (
      <Wrapper>
        <Loading>Loading…</Loading>
      </Wrapper>
    );
  }

  const maxCount = Math.max(...data.actual, ...data.gauss);

  return (
    <Wrapper>
      <Grid>
        <Panel>
          <PanelHeading>
            <PanelTitle>Typical (Gaussian)</PanelTitle>
            <Entropy>{data.entropy_bits.gauss.toFixed(2)} bits/weight</Entropy>
          </PanelHeading>
          <Histogram
            bins={data.bins}
            counts={data.gauss}
            maxCount={maxCount}
            color="var(--text-muted)"
          />
        </Panel>
        <Panel>
          <PanelHeading>
            <PanelTitle>Our C1a-shaped</PanelTitle>
            <Entropy>{data.entropy_bits.actual.toFixed(2)} bits/weight</Entropy>
          </PanelHeading>
          <Histogram
            bins={data.bins}
            counts={data.actual}
            maxCount={maxCount}
            color="var(--accent)"
          />
        </Panel>
      </Grid>
      <Caption>
        Both histograms cover the full INT8 range (−127 to 127) with one bin per
        integer, and both contain the same 226,512 weights. Left: a Gaussian
        distribution with the same overall stddev as our weights, which is what
        a normally-trained INT8-quantized network would look like. Right: the
        actual post-C1a distribution from our shipped archive. The marginal
        entropy only drops by a small amount, but brotli&apos;s context coding
        rewards the sharp local peaks and asymmetric bias far more than the
        marginal entropy suggests.
      </Caption>
    </Wrapper>
  );
};

export default WeightDistribution;
